import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus, Trash2, Download, Upload, Mail, Building2, ChevronDown, FileDown,
  Loader2, Pencil, X, Save, CalendarClock, LayoutGrid, List, TrendingUp,
  BookOpen, Package, BellRing, Globe,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import LeadDetailModal from './LeadDetailModal';
import PipelineKanban from './PipelineKanban';
import BrancheBriefingModal from './BrancheBriefingModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_OPTIONS = [
  { value: 'krankenhaus', label: 'Krankenhaus (≥ 900 Betten)' },
  { value: 'stadtverwaltung', label: 'Stadtverwaltung (10k–75k EW)' },
  { value: 'versicherung', label: 'Versicherung' },
  { value: 'bank_sparkasse', label: 'Bank / Sparkasse' },
  { value: 'stadtwerke_energie', label: 'Stadtwerke / Energie' },
  { value: 'versandhandel', label: 'Versandhandel' },
  { value: 'krankenkasse', label: 'Krankenkasse' },
  { value: 'verlag_medien', label: 'Verlag / Medien' },
  { value: 'kanzlei', label: 'Kanzlei' },
  { value: 'industrie', label: 'Industrieunternehmen' },
  { value: 'lettershop_druckerei', label: 'Lettershop / Druckerei' },
  { value: 'it_telekom', label: 'IT / Telekommunikation' },
  { value: 'einzelhandel', label: 'Einzelhandel' },
  { value: 'labor', label: 'MVZ-Labor / Laborkette' },
  { value: 'sonstige', label: 'Sonstige' },
];

const STATE_OPTIONS = [
  { value: 'BW', label: 'Baden-Württemberg' },
  { value: 'BY', label: 'Bayern' },
  { value: 'BE', label: 'Berlin' },
  { value: 'BB', label: 'Brandenburg' },
  { value: 'HB', label: 'Bremen' },
  { value: 'HH', label: 'Hamburg' },
  { value: 'HE', label: 'Hessen' },
  { value: 'MV', label: 'Mecklenburg-Vorpommern' },
  { value: 'NI', label: 'Niedersachsen' },
  { value: 'NW', label: 'Nordrhein-Westfalen' },
  { value: 'RP', label: 'Rheinland-Pfalz' },
  { value: 'SL', label: 'Saarland' },
  { value: 'SN', label: 'Sachsen' },
  { value: 'ST', label: 'Sachsen-Anhalt' },
  { value: 'SH', label: 'Schleswig-Holstein' },
  { value: 'TH', label: 'Thüringen' },
];

const PLZ_REGION_OPTIONS = [
  { value: '0', label: '0 — Sachsen / Brandenburg / SA' },
  { value: '1', label: '1 — Berlin / Brandenburg' },
  { value: '2', label: '2 — Hamburg / SH / Bremen' },
  { value: '3', label: '3 — Niedersachsen / Hessen' },
  { value: '4', label: '4 — NRW (West)' },
  { value: '5', label: '5 — NRW / RP' },
  { value: '6', label: '6 — Hessen / RP / Saarland' },
  { value: '7', label: '7 — Stuttgart / BW' },
  { value: '8', label: '8 — München / Süd-Bayern' },
  { value: '9', label: '9 — Nord-Bayern / Sachsen' },
];

const STATUS_OPTIONS = [
  { value: 'new', label: 'Neu', color: 'bg-slate-100 text-slate-700' },
  { value: 'researched', label: 'Recherchiert', color: 'bg-blue-50 text-blue-700' },
  { value: 'letter_sent', label: 'Brief versendet', color: 'bg-amber-50 text-amber-700' },
  { value: 'follow_up', label: 'Nachfass', color: 'bg-orange-50 text-orange-700' },
  { value: 'meeting', label: 'Erstgespräch', color: 'bg-purple-50 text-purple-700' },
  { value: 'mandate', label: 'Mandat', color: 'bg-green-50 text-green-700' },
  { value: 'closed', label: 'Abgeschlossen', color: 'bg-slate-100 text-slate-500' },
];

const SALUTATION_OPTIONS = [
  { value: 'neutral', label: 'Neutral (Sehr geehrte Damen und Herren)' },
  { value: 'herr', label: 'Herr' },
  { value: 'frau', label: 'Frau' },
];

const EMPTY_FORM = {
  type: 'krankenhaus',
  state: 'BW',
  name: '',
  size_metric: '',
  street: '',
  zip: '',
  city: '',
  salutation: 'neutral',
  contact_person: '',
  contact_role: '',
  phone_office: '',
  phone_mobile: '',
  email1: '',
  email2: '',
  website: '',
  notes: '',
  volume_hint: '',
  data_year: '',
};

const OutreachTab = ({ password }) => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPlz, setFilterPlz] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [filterLevel, setFilterLevel] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [savingId, setSavingId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [importType, setImportType] = useState('krankenhaus');
  const [importState, setImportState] = useState('BW');
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [detailLeadId, setDetailLeadId] = useState(null);
  const [filterDue, setFilterDue] = useState('all'); // all | today | overdue | upcoming
  const [viewMode, setViewMode] = useState('list'); // list | kanban
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showBrancheBriefing, setShowBrancheBriefing] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);

  const headers = useMemo(
    () => ({ 'x-admin-password': password }),
    [password]
  );

  const fetchTargets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set('type', filterType);
      if (filterState) params.set('state', filterState);
      if (filterStatus) params.set('status', filterStatus);
      if (filterPlz) params.set('plz_region', filterPlz);
      if (filterRegion) params.set('region', filterRegion);
      if (filterLevel) params.set('level', filterLevel);
      const res = await fetch(`${API}/admin/outreach/targets?${params}`, { headers });
      const data = await res.json();
      setTargets(data.targets || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTargets();
  }, [filterType, filterState, filterStatus, filterPlz, filterRegion, filterLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTarget = async (e) => {
    e?.preventDefault?.();
    if (!form.name) return;
    setSavingId('form');
    try {
      const body = {
        ...form,
        size_metric: parseInt(form.size_metric, 10) || 0,
        volume_hint: parseInt(form.volume_hint, 10) || 0,
      };
      const url = editingId
        ? `${API}/admin/outreach/targets/${editingId}`
        : `${API}/admin/outreach/targets`;
      const method = editingId ? 'PUT' : 'POST';
      await fetch(url, {
        method,
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      setForm(EMPTY_FORM);
      setEditingId(null);
      setShowForm(false);
      fetchTargets();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingId(null);
    }
  };

  const startEdit = (t) => {
    setForm({
      type: t.type,
      state: t.state,
      name: t.name || '',
      size_metric: String(t.size_metric || ''),
      street: t.street || '',
      zip: t.zip || '',
      city: t.city || '',
      salutation: t.salutation || 'neutral',
      contact_person: t.contact_person || '',
      contact_role: t.contact_role || '',
      phone_office: t.phone_office || '',
      phone_mobile: t.phone_mobile || '',
      email1: t.email1 || '',
      email2: t.email2 || '',
      website: t.website || '',
      notes: t.notes || '',
      volume_hint: String(t.volume_hint || ''),
      data_year: t.data_year || '',
    });
    setEditingId(t.id);
    setShowForm(true);
  };

  const updateStatus = async (target, newStatus) => {
    setSavingId(target.id);
    try {
      await fetch(`${API}/admin/outreach/targets/${target.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      fetchTargets();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingId(null);
    }
  };

  const deleteTarget = async (id) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    try {
      await fetch(`${API}/admin/outreach/targets/${id}`, {
        method: 'DELETE', headers,
      });
      fetchTargets();
    } catch (err) {
      console.error(err);
    }
  };

  const generateLetter = async (target) => {
    setGeneratingId(target.id);
    try {
      const res = await fetch(
        `${API}/admin/outreach/targets/${target.id}/letter?bundle=true`,
        { headers }
      );
      if (!res.ok) throw new Error('Brief-Generierung fehlgeschlagen');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = target.name.replace(/[^a-z0-9]+/gi, '-').slice(0, 60);
      a.download = `Anschreiben-${safeName}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      // Auto-update status to letter_sent if currently new/researched
      if (['new', 'researched'].includes(target.status)) {
        await updateStatus(target, 'letter_sent');
      } else {
        // Auch ohne Status-Wechsel die Liste neu laden, damit
        // das Auto-Stempel-Datum "Erstkontakt am" sichtbar wird.
        fetchTargets();
      }
    } catch (err) {
      console.error(err);
      alert('Fehler beim Generieren des Briefs.');
    } finally {
      setGeneratingId(null);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAllVisible = () => {
    setSelectedIds(new Set(visibleTargets.map((t) => t.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const sendReminderNow = async () => {
    if (!window.confirm('Erinnerungsmail jetzt an die konfigurierte Admin-Adresse senden?')) return;
    setSendingReminder(true);
    try {
      const res = await fetch(`${API}/admin/outreach/reminders/run-now`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      if (data.sent) {
        alert(`✅ Erinnerung versandt: ${data.count} Wiedervorlage${data.count === 1 ? '' : 'n'} heute fällig.`);
      } else if (data.reason === 'no_followups') {
        alert('Heute keine Wiedervorlagen fällig — keine Mail versandt.');
      } else {
        alert('Mail konnte nicht versandt werden. Logs prüfen.');
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setSendingReminder(false);
    }
  };

  const handleBulkLetters = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(
      `${selectedIds.size} Anschreiben (inkl. Whitepaper) als ZIP generieren?`
    )) return;
    setBulkLoading(true);
    try {
      const res = await fetch(`${API}/admin/outreach/targets/bulk-letters`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids: Array.from(selectedIds),
          bundle: true,
          auto_mark_sent: true,
        }),
      });
      if (!res.ok) throw new Error('Bulk-Generierung fehlgeschlagen');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Anschreiben-Bulk-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      clearSelection();
      fetchTargets();
    } catch (err) {
      alert(err.message);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(
        `${API}/admin/outreach/targets/import-csv?type=${importType}&state=${importState}`,
        { method: 'POST', headers, body: fd }
      );
      // Body genau einmal lesen, dann sicher als JSON parsen. So entsteht nie ein
      // "body stream already read"-Fehler, auch wenn der Server unerwarteten Text
      // (z. B. eine HTML-Fehlerseite) zurückliefert.
      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch {
        throw new Error(`Server antwortete nicht im JSON-Format (HTTP ${res.status}). Erste 200 Zeichen: ${raw.slice(0, 200)}`);
      }
      if (!res.ok) {
        throw new Error(data.detail || `Import-Fehler (HTTP ${res.status})`);
      }
      setImportMsg(
        `${data.inserted} Einträge importiert${
          data.skipped?.length ? ` · ${data.skipped.length} übersprungen` : ''
        }`
      );
      fetchTargets();
    } catch (err) {
      console.error(err);
      setImportMsg(err.message || 'Import fehlgeschlagen.');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const downloadCsvTemplate = () => {
    // Windows-1252 + Semikolon = saubere Öffnung in Excel/Outlook ohne
    // "Sicherheitshinweis: möglicherweise beschädigte Datei".
    const csvText =
      'name;size_metric;street;zip;city;state;phone;region;level;salutation;contact_person;contact_role;volume_hint;data_year;notes\n' +
      'Beispiel Versicherung AG;0;Beispielstr 1;70173;Stuttgart;BW;0711-123456;sued;mittelgross;herr;Max Mustermann;Vorstand Finanzen;180000;2015;Recherche aus 2015\n' +
      'Beispiel Verlag GmbH;0;Verlagsstr 2;80331;München;BY;089-99999;sued;mittelgross;frau;Maria Beispiel;Geschäftsführung;35000;2018;\n' +
      'Beispiel Druckerei KG;0;Druckweg 5;10115;Berlin;BE;030-22222;nord;klein;neutral;;Geschäftsführung;2500000;2015;Großer Lettershop\n';
    // String → Windows-1252 bytes (non-mapped chars → "?" — Umlaute/€ bleiben sauber)
    const bytes = new Uint8Array(csvText.length);
    for (let i = 0; i < csvText.length; i++) {
      const code = csvText.charCodeAt(i);
      // Quick map for common German specials
      const map = { 0x20AC: 0x80, 0x201A: 0x82, 0x201E: 0x84, 0x2026: 0x85,
                    0x2018: 0x91, 0x2019: 0x92, 0x201C: 0x93, 0x201D: 0x94,
                    0x2013: 0x96, 0x2014: 0x97 };
      bytes[i] = code < 256 ? code : (map[code] || 0x3F);
    }
    const blob = new Blob([bytes], { type: 'text/csv;charset=windows-1252;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'outreach-vorlage.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = useMemo(() => {
    const byStatus = {};
    targets.forEach((t) => {
      byStatus[t.status] = (byStatus[t.status] || 0) + 1;
    });
    return byStatus;
  }, [targets]);

  // Follow-up bucket helpers
  const todayStr = new Date().toISOString().slice(0, 10);
  const followUpBucket = (t) => {
    const f = (t.follow_up_date || '').trim();
    if (!f) return 'none';
    if (f < todayStr) return 'overdue';
    if (f === todayStr) return 'today';
    return 'upcoming';
  };

  const dueStats = useMemo(() => {
    const c = { today: 0, overdue: 0, upcoming: 0 };
    targets.forEach((t) => {
      const b = followUpBucket(t);
      if (c[b] !== undefined) c[b] += 1;
    });
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets]);

  const visibleTargets = useMemo(() => {
    if (filterDue === 'all') return targets;
    return targets.filter((t) => followUpBucket(t) === filterDue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets, filterDue]);

  const pipelineStats = useMemo(() => {
    const sent = (stats.letter_sent || 0) + (stats.follow_up || 0) + (stats.meeting || 0) + (stats.mandate || 0);
    const conv = sent > 0 ? Math.round(((stats.mandate || 0) / sent) * 1000) / 10 : 0;
    return { sent, conversion: conv };
  }, [stats]);

  return (
    <div className="space-y-6" data-testid="outreach-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Zielgruppen-Generator ({targets.length})
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Neukunden-Outreach: Krankenhäuser & Stadtverwaltungen in BW, BY, RP
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={sendReminderNow}
            disabled={sendingReminder}
            data-testid="reminder-run-now-btn"
            title="Erinnerung für heute fällige Wiedervorlagen sofort per Mail senden"
            className="bg-amber-50 hover:bg-amber-100 border-amber-200 text-amber-900 hover:text-amber-950"
          >
            {sendingReminder ? (
              <Loader2 size={14} className="mr-2 animate-spin" />
            ) : (
              <BellRing size={14} className="mr-2 text-amber-600" />
            )}
            Wiedervorlage jetzt prüfen
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowBrancheBriefing(true)}
            data-testid="branche-briefing-open-btn"
            className="bg-sky-50 hover:bg-sky-100 border-sky-200 text-sky-900 hover:text-sky-950"
          >
            <BookOpen size={14} className="mr-2 text-sky-600" /> Branchen-Briefing
          </Button>
          <div className="inline-flex bg-slate-100 rounded-md p-0.5" data-testid="view-mode-toggle">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-slate-200 text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:bg-white hover:text-slate-900'
              }`}
              data-testid="view-mode-list"
            >
              <List size={13} />
              Liste
            </button>
            <button
              type="button"
              onClick={() => setViewMode('kanban')}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                viewMode === 'kanban'
                  ? 'text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:bg-white hover:text-slate-900'
              }`}
              style={viewMode === 'kanban' ? { backgroundColor: '#FFE7D5' } : undefined}
              data-testid="view-mode-kanban"
            >
              <LayoutGrid size={13} />
              Pipeline
            </button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(!showForm); }}
            data-testid="outreach-add-btn"
          >
            <Plus size={14} className="mr-2" /> Neuer Eintrag
          </Button>
        </div>
      </div>

      {/* Stats badges */}
      {targets.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          {STATUS_OPTIONS.map((s) => (
            stats[s.value] ? (
              <span
                key={s.value}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${s.color}`}
              >
                {s.label}: <span className="font-bold">{stats[s.value]}</span>
              </span>
            ) : null
          ))}
          {(dueStats.today + dueStats.overdue + dueStats.upcoming) > 0 && (
            <span className="text-slate-300 mx-1">·</span>
          )}
          {dueStats.overdue > 0 && (
            <button
              onClick={() => setFilterDue(filterDue === 'overdue' ? 'all' : 'overdue')}
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-all ${
                filterDue === 'overdue'
                  ? 'bg-red-600 text-white ring-2 ring-red-300'
                  : 'bg-red-50 text-red-700 hover:bg-red-100'
              }`}
              data-testid="filter-due-overdue"
            >
              <CalendarClock size={12} />
              Überfällig: <span className="font-bold">{dueStats.overdue}</span>
            </button>
          )}
          {dueStats.today > 0 && (
            <button
              onClick={() => setFilterDue(filterDue === 'today' ? 'all' : 'today')}
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-all ${
                filterDue === 'today'
                  ? 'bg-amber-600 text-white ring-2 ring-amber-300'
                  : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
              }`}
              data-testid="filter-due-today"
            >
              <CalendarClock size={12} />
              Heute fällig: <span className="font-bold">{dueStats.today}</span>
            </button>
          )}
          {dueStats.upcoming > 0 && (
            <button
              onClick={() => setFilterDue(filterDue === 'upcoming' ? 'all' : 'upcoming')}
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-all ${
                filterDue === 'upcoming'
                  ? 'bg-blue-600 text-white ring-2 ring-blue-300'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
              data-testid="filter-due-upcoming"
            >
              <CalendarClock size={12} />
              Kommend: <span className="font-bold">{dueStats.upcoming}</span>
            </button>
          )}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <form
          onSubmit={saveTarget}
          className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm space-y-3"
          data-testid="outreach-form"
        >
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-slate-900">
              {editingId ? 'Eintrag bearbeiten' : 'Neuer Zieleintrag'}
            </h3>
            <button
              type="button"
              onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }}
              className="text-slate-400 hover:text-slate-700"
            >
              <X size={18} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <select
              className="rounded-md border border-slate-200 px-3 py-2 text-sm bg-white"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              className="rounded-md border border-slate-200 px-3 py-2 text-sm bg-white"
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
            >
              {STATE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <Input
              placeholder="Name der Einrichtung *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <Input
              type="number"
              placeholder={
                form.type === 'krankenhaus'
                  ? 'Anzahl Betten (optional)'
                  : form.type === 'stadtverwaltung'
                  ? 'Einwohnerzahl (optional)'
                  : 'Mitarbeiter / Größe (optional)'
              }
              value={form.size_metric}
              onChange={(e) => setForm({ ...form, size_metric: e.target.value })}
            />
            <Input
              placeholder="Straße"
              value={form.street}
              onChange={(e) => setForm({ ...form, street: e.target.value })}
            />
            <div className="grid grid-cols-3 gap-3">
              <Input
                placeholder="PLZ"
                value={form.zip}
                onChange={(e) => setForm({ ...form, zip: e.target.value })}
              />
              <div className="col-span-2">
                <Input
                  placeholder="Stadt"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>
            </div>
            <select
              className="rounded-md border border-slate-200 px-3 py-2 text-sm bg-white"
              value={form.salutation}
              onChange={(e) => setForm({ ...form, salutation: e.target.value })}
            >
              {SALUTATION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <Input
              placeholder="Ansprechpartner (Vor- und Nachname, optional)"
              value={form.contact_person}
              onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="Telefon Büro"
                value={form.phone_office}
                onChange={(e) => setForm({ ...form, phone_office: e.target.value })}
                data-testid="form-phone-office"
              />
              <Input
                placeholder="Telefon Mobil"
                value={form.phone_mobile}
                onChange={(e) => setForm({ ...form, phone_mobile: e.target.value })}
                data-testid="form-phone-mobile"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="email"
                placeholder="E-Mail 1"
                value={form.email1}
                onChange={(e) => setForm({ ...form, email1: e.target.value })}
                data-testid="form-email1"
              />
              <Input
                type="email"
                placeholder="E-Mail 2"
                value={form.email2}
                onChange={(e) => setForm({ ...form, email2: e.target.value })}
                data-testid="form-email2"
              />
            </div>
            <Input
              placeholder="Homepage (z. B. www.beispiel.de)"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              data-testid="form-website"
            />
            <Input
              placeholder="Rolle (z. B. Geschäftsführung)"
              value={form.contact_role}
              onChange={(e) => setForm({ ...form, contact_role: e.target.value })}
            />
            <Input
              type="number"
              placeholder="Sendungen/Jahr (intern, nicht im Brief)"
              value={form.volume_hint}
              onChange={(e) => setForm({ ...form, volume_hint: e.target.value })}
            />
            <Input
              placeholder="Datenstand (z. B. 2015)"
              value={form.data_year}
              onChange={(e) => setForm({ ...form, data_year: e.target.value })}
            />
          </div>
          <Textarea
            placeholder="Notizen (intern)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="min-h-[60px]"
          />
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={savingId === 'form'}
              className="bg-slate-800 hover:bg-slate-700"
              data-testid="outreach-save-btn"
            >
              {savingId === 'form' ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : (
                <Save size={14} className="mr-2" />
              )}
              {editingId ? 'Aktualisieren' : 'Speichern'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      )}

      {/* CSV Import block */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex flex-col md:flex-row items-start md:items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Upload size={16} className="text-slate-500" />
          CSV-Import:
        </div>
        <select
          className="rounded-md border border-slate-200 px-2 py-1.5 text-sm bg-white"
          value={importType}
          onChange={(e) => setImportType(e.target.value)}
        >
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          className="rounded-md border border-slate-200 px-2 py-1.5 text-sm bg-white"
          value={importState}
          onChange={(e) => setImportState(e.target.value)}
        >
          {STATE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <label className="cursor-pointer inline-flex items-center gap-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:border-slate-400 rounded-md px-3 py-1.5">
          {importing ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Upload size={14} />
          )}
          CSV-Datei wählen
          <input
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleImport}
            disabled={importing}
            data-testid="outreach-csv-import"
          />
        </label>
        <Button
          variant="ghost"
          size="sm"
          onClick={downloadCsvTemplate}
          className="text-slate-600"
        >
          <FileDown size={14} className="mr-1.5" /> Vorlage
        </Button>
        {importMsg && (
          <span className="text-sm text-slate-600 ml-2">{importMsg}</span>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">
          Filter:
        </span>
        <select
          className="rounded-md border border-slate-200 px-2 py-1.5 text-sm bg-white"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="">Alle Branchen</option>
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          className="rounded-md border border-slate-200 px-2 py-1.5 text-sm bg-white"
          value={filterState}
          onChange={(e) => setFilterState(e.target.value)}
        >
          <option value="">Alle Bundesländer</option>
          {STATE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          className="rounded-md border border-slate-200 px-2 py-1.5 text-sm bg-white"
          value={filterPlz}
          onChange={(e) => setFilterPlz(e.target.value)}
        >
          <option value="">Alle PLZ-Regionen</option>
          {PLZ_REGION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          className="rounded-md border border-slate-200 px-2 py-1.5 text-sm bg-white"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">Alle Status</option>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          className="rounded-md border border-slate-200 px-2 py-1.5 text-sm bg-white"
          value={filterRegion}
          onChange={(e) => setFilterRegion(e.target.value)}
          data-testid="outreach-filter-region"
        >
          <option value="">Alle Regionen</option>
          <option value="sued">Süd</option>
          <option value="nord">Nord</option>
          <option value="west">West</option>
          <option value="ost">Ost</option>
          <option value="mitte">Mitte</option>
        </select>
        <select
          className="rounded-md border border-slate-200 px-2 py-1.5 text-sm bg-white"
          value={filterLevel}
          onChange={(e) => setFilterLevel(e.target.value)}
          data-testid="outreach-filter-level"
        >
          <option value="">Alle Ebenen</option>
          <option value="mittelgross">Mittelgroß</option>
          <option value="klein">Klein</option>
        </select>
      </div>

      {/* Pipeline mini-stats panel (only visible when there are leads) */}
      {targets.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="pipeline-stats-panel">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              Leads gesamt
            </div>
            <div className="text-2xl font-semibold text-slate-900 mt-0.5">
              {targets.length}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              In Aktion
            </div>
            <div className="text-2xl font-semibold text-slate-900 mt-0.5">
              {pipelineStats.sent}
              <span className="text-xs text-slate-400 font-normal ml-1">
                {pipelineStats.sent === 1 ? 'Brief draußen' : 'Briefe draußen'}
              </span>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              Erstgespräche
            </div>
            <div className="text-2xl font-semibold text-purple-700 mt-0.5">
              {stats.meeting || 0}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1">
              <TrendingUp size={11} />
              Conversion → Mandat
            </div>
            <div className="text-2xl font-semibold text-green-700 mt-0.5">
              {pipelineStats.conversion}%
              <span className="text-xs text-slate-400 font-normal ml-1">
                ({stats.mandate || 0})
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Main view: Table OR Kanban */}
      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" />
        </div>
      ) : viewMode === 'kanban' ? (
        targets.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
            <Building2 size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Noch keine Einträge.</p>
          </div>
        ) : (
          <PipelineKanban
            targets={visibleTargets}
            onChangeStatus={updateStatus}
            onOpenLead={setDetailLeadId}
          />
        )
      ) : visibleTargets.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <Building2 size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">
            {targets.length === 0 ? 'Noch keine Einträge.' : 'Keine Einträge im aktuellen Filter.'}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {targets.length === 0
              ? 'Erstellen Sie einen ersten Eintrag oder nutzen Sie den CSV-Import.'
              : 'Entferne den Filter, um alle Einträge zu sehen.'}
          </p>
        </div>
      ) : (
        <>
          {/* Bulk action toolbar */}
          {selectedIds.size > 0 && (
            <div
              className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex flex-wrap items-center gap-3"
              data-testid="bulk-toolbar"
            >
              <Package size={16} className="text-orange-700" />
              <span className="text-sm font-medium text-orange-900">
                {selectedIds.size} ausgewählt
              </span>
              <Button
                size="sm"
                onClick={handleBulkLetters}
                disabled={bulkLoading}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="bulk-letters-btn"
              >
                {bulkLoading ? (
                  <>
                    <Loader2 size={13} className="mr-1.5 animate-spin" />
                    ZIP wird erstellt …
                  </>
                ) : (
                  <>
                    <Download size={13} className="mr-1.5" />
                    Briefe als ZIP exportieren
                  </>
                )}
              </Button>
              <button
                onClick={clearSelection}
                className="text-xs text-orange-800 underline hover:text-orange-900"
              >
                Auswahl löschen
              </button>
            </div>
          )}

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={
                        visibleTargets.length > 0 &&
                        visibleTargets.every((t) => selectedIds.has(t.id))
                      }
                      onChange={(e) =>
                        e.target.checked ? selectAllVisible() : clearSelection()
                      }
                      className="cursor-pointer"
                      data-testid="bulk-select-all"
                    />
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Einrichtung</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Branche</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Volumen (intern)</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Erstkontakt am</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Wiedervorlage</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {visibleTargets.map((t) => {
                  const bucket = followUpBucket(t);
                  const activityCount = (t.activities || []).length;
                  const checked = selectedIds.has(t.id);
                  return (
                  <tr
                    key={t.id}
                    className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                    data-testid={`outreach-row-${t.id}`}
                  >
                    <td className="px-3 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelect(t.id)}
                        className="cursor-pointer"
                        data-testid={`bulk-select-${t.id}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setDetailLeadId(t.id)}
                          className="text-left font-medium text-slate-900 hover:text-orange-600 transition-colors"
                          data-testid={`outreach-open-detail-${t.id}`}
                        >
                          {t.name}
                        </button>
                        {t.website && (
                          <a
                            href={/^https?:\/\//i.test(t.website) ? t.website : `https://${t.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex p-1 rounded hover:bg-orange-50 transition-colors"
                            title={`Webseite öffnen: ${t.website}`}
                            data-testid={`outreach-website-${t.id}`}
                          >
                            <Globe size={12} className="text-slate-400 hover:text-orange-600" />
                          </a>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{t.state}</span>
                        {t.plz_region ? (<><span>·</span><span>PLZ {t.plz_region}</span></>) : null}
                        {t.region ? (
                          <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-medium uppercase tracking-wide">
                            {t.region}
                          </span>
                        ) : null}
                        {t.level ? (
                          <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-medium uppercase tracking-wide">
                            {t.level === 'mittelgross' ? 'mittelgroß' : t.level}
                          </span>
                        ) : null}
                        {t.contact_person ? (<><span>·</span><span>{t.contact_person}</span></>) : null}
                        {t.phone_office ? (<><span>·</span><span>☎ {t.phone_office}</span></>) : null}
                        {activityCount > 0 ? (
                          <>
                            <span>·</span>
                            <span className="text-slate-700 font-medium">
                              {activityCount} Aktivität{activityCount === 1 ? '' : 'en'}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs">
                      {TYPE_OPTIONS.find((o) => o.value === t.type)?.label || t.type}
                      {t.size_metric ? (
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          {t.size_metric.toLocaleString('de-DE')}
                          {t.type === 'krankenhaus' ? ' Betten' : t.type === 'stadtverwaltung' ? ' EW' : ''}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs">
                      {t.volume_hint ? (
                        <>
                          <div className="font-medium text-slate-800">
                            ~ {t.volume_hint.toLocaleString('de-DE')} / Jahr
                          </div>
                          {t.data_year ? (
                            <div className="text-[11px] text-slate-400 mt-0.5">
                              Stand {t.data_year}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs" data-testid={`outreach-firstcontact-${t.id}`}>
                      {t.first_contact_date ? (
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-800">
                            {new Date(t.first_contact_date).toLocaleDateString('de-DE')}
                          </span>
                          {t.first_contact_channel ? (
                            <span className="text-[11px] text-slate-400 mt-0.5 capitalize">
                              {t.first_contact_channel.replace('_', ' + ')}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-slate-300">–</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {t.follow_up_date ? (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-medium ${
                            bucket === 'overdue'
                              ? 'bg-red-50 text-red-700'
                              : bucket === 'today'
                              ? 'bg-amber-50 text-amber-700'
                              : 'bg-blue-50 text-blue-700'
                          }`}
                          title={
                            bucket === 'overdue'
                              ? 'Überfällig'
                              : bucket === 'today'
                              ? 'Heute fällig'
                              : 'Geplant'
                          }
                        >
                          <CalendarClock size={11} />
                          {new Date(t.follow_up_date).toLocaleDateString('de-DE')}
                        </span>
                      ) : (
                        <span className="text-slate-300">–</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        className={`text-xs font-medium px-2.5 py-1 rounded-full border-0 cursor-pointer ${
                          STATUS_OPTIONS.find((s) => s.value === t.status)?.color || 'bg-slate-100 text-slate-700'
                        }`}
                        value={t.status}
                        onChange={(e) => updateStatus(t, e.target.value)}
                        disabled={savingId === t.id}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => generateLetter(t)}
                          disabled={generatingId === t.id}
                          title="Anschreiben + Whitepaper als PDF generieren"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-50"
                          data-testid={`outreach-letter-${t.id}`}
                          style={{
                            backgroundColor: 'rgba(255, 162, 102, 0.15)',
                          }}
                        >
                          {generatingId === t.id ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : (
                            <Download size={13} />
                          )}
                          Brief
                        </button>
                        <button
                          onClick={() => startEdit(t)}
                          className="p-1.5 hover:bg-slate-100 rounded-md transition-colors"
                          title="Bearbeiten"
                        >
                          <Pencil size={13} className="text-slate-500" />
                        </button>
                        <button
                          onClick={() => deleteTarget(t.id)}
                          className="p-1.5 hover:bg-red-50 rounded-md transition-colors"
                          title="Löschen"
                        >
                          <Trash2 size={13} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      {detailLeadId && (
        <LeadDetailModal
          leadId={detailLeadId}
          password={password}
          onClose={() => setDetailLeadId(null)}
          onChange={fetchTargets}
        />
      )}

      {showBrancheBriefing && (
        <BrancheBriefingModal
          password={password}
          onClose={() => setShowBrancheBriefing(false)}
        />
      )}
    </div>
  );
};

export default OutreachTab;
